import { useMemo } from 'react';
import { MediaLibraryItem, MediaLibraryItemFileType } from 'types/MediaLibrary';
import { useMediaAttachmentsUploader } from 'utils/hooks/useMediaAttachmentsUploader';
import { useTranslation } from 'react-i18next';
import { FileSource } from 'app/components/fileSourceInput/types/fileSource';
import { Icon } from '@kontentino/ui';
import { faLaptop } from '@fortawesome/pro-regular-svg-icons/faLaptop';
import { faPhotoFilm } from '@fortawesome/pro-regular-svg-icons/faPhotoFilm';
import { UploadSequence } from 'types/RunningUpload';
import useBoolean from 'utils/hooks/useBoolean';
import Logger from 'utils/logger';
import { useDefaultPageAlbumId } from 'app/modules/posts/hooks/useDefaultPageAlbumId';
import { useIsMediaLibraryEnabled } from 'app/hooks/useIsMediaLibraryEnabled';

export const DEFAULT_FILE_SOURCES = ['media-library', 'upload'] as const;
export type FileSourceType = (typeof DEFAULT_FILE_SOURCES)[number];

export type UseFileSourcesProps = {
  page: {
    id: number;
    type: number;
  };
  albumId?: string;
  onChange: (media: MediaLibraryItem[]) => void;
  maxFiles: number;
  allowedFiles: MediaLibraryItemFileType[];
  uploadSequence?: UploadSequence;
  sources?: readonly FileSourceType[];
};

export function useFileSource({
  page,
  albumId,
  maxFiles,
  allowedFiles,
  onChange,
  uploadSequence,
  sources,
}: UseFileSourcesProps) {
  const isMediaLibraryEnabled = useIsMediaLibraryEnabled();
  const defaultAlbumId = useDefaultPageAlbumId(page.id, !isMediaLibraryEnabled);
  const [isShown, setIsShown] = useBoolean(false);
  const { t } = useTranslation();
  const mediaUploader = useMediaAttachmentsUploader({
    page,
    destination: isMediaLibraryEnabled
      ? { library: 'assets', albumId }
      : { library: 'legacy', albumId: defaultAlbumId },
    onMediaUploaded: (item) => {
      if (item.result) {
        onChange([item.result]);
      }
    },
    allowedFiles,
    maxFiles,
    uploadSequence,
  });

  function getFileSources() {
    const enabled = sources ?? DEFAULT_FILE_SOURCES;
    const deviceSource: FileSource = {
      shortLabel: t('fromDevice'),
      label: (
        <>
          <Icon icon={faLaptop} />
          {t('fromYourDevice')}
        </>
      ),
      onClick: mediaUploader.dropzone.open,
      attributes: {
        dataName: 'file-source-device-option',
        dataCy: 'file-source-device-option',
      },
    };
    const mediaLibrarySource: FileSource = {
      label: (
        <>
          <Icon icon={faPhotoFilm} />
          {t('fromYourMediaLibrary')}
        </>
      ),
      onClick: setIsShown.on,
      attributes: {
        dataName: 'file-source-media-library-option',
        dataCy: 'file-source-media-library-option',
      },
    };
    const result: FileSource[] = [
      ...(enabled.includes('upload') ? [deviceSource] : []),
      ...(enabled.includes('media-library') ? [mediaLibrarySource] : []),
    ];
    if (!result.length) {
      Logger.warn(
        'useFileSource: no valid sources provided, falling back to upload source',
      );
      return [deviceSource] as [FileSource, ...FileSource[]];
    }
    return result as [FileSource, ...FileSource[]];
  }

  const fileSources = useMemo(
    () => getFileSources(),
    [sources, t, mediaUploader.dropzone.open, setIsShown.on], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {
    fileSources,
    mediaLibrary: {
      isShown,
      toggle: setIsShown.toggle,
      show: setIsShown.on,
      hide: setIsShown.off,
    },
    mediaUploader,
    params: {
      page,
      albumId,
      maxFiles,
      allowedFiles,
      onChange,
      uploadSequence,
    },
  };
}
